import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Alert,
} from "@mui/material";
import { getFirestore, collection, query, orderBy, getDocs, doc, getDoc, limit, startAfter, QueryDocumentSnapshot } from "firebase/firestore";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmailIcon from "@mui/icons-material/Email";
import {
  OrderStatus,
  updateOrderStatus,
  getStatusLabel,
  getStatusColor,
  isValidStatusTransition,
} from "../../utils/firebaseUtils";
import { useAuth } from "../../firebase";

const ALL_STATUSES: OrderStatus[] = [
  "pending",
  "awaiting-info",
  "in-progress",
  "ready-for-download",
  "shipped",
  "delivered",
  "canceled",
];

// Define the Order interface
interface Order {
  id: string;
  paypalOrderId: string;
  status: OrderStatus;
  amount: number;
  currency: string;
  createdAt: string;
  userId: string;
  items: Array<{
    productId: string;
    name: string;
    quantity: number;
    price: number;
    selectedOptions: any;
  }>;
  formData?: any;
  statusHistory?: Array<{
    fromStatus: string;
    toStatus: string;
    timestamp: any;
    reason: string;
    notes?: string;
  }>;
  // Customer information (fetched separately)
  customerEmail?: string;
  customerName?: string;
}

const AdminDashboard: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusSuccess, setStatusSuccess] = useState<string | null>(null);
  const [statusNotes, setStatusNotes] = useState("");
  const db = getFirestore();
  const { user } = useAuth();

  useEffect(() => {
    fetchOrders(rowsPerPage);
  }, []);

  const fetchOrders = async (pageSize: number, afterDoc?: QueryDocumentSnapshot) => {
    try {
      setLoading(true);
      const ordersRef = collection(db, "orders");

      let q;
      if (afterDoc) {
        q = query(ordersRef, orderBy("createdAt", "desc"), startAfter(afterDoc), limit(pageSize));
      } else {
        q = query(ordersRef, orderBy("createdAt", "desc"), limit(pageSize));
      }

      const snapshot = await getDocs(q);

      // Track if there are more results
      setHasMore(snapshot.docs.length === pageSize);

      // Track last document for next page
      if (snapshot.docs.length > 0) {
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      }

      const ordersList = await Promise.all(
        snapshot.docs.map(async (orderDoc) => {
          const orderData = orderDoc.data();

          // Fetch customer information from users collection
          let customerEmail = "";
          let customerName = "";

          if (orderData.userId) {
            try {
              const userRef = doc(db, "users", orderData.userId);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                const userData = userSnap.data();
                customerEmail = userData.email || "";
                customerName = userData.name || userData.displayName || "";
              }
            } catch (userError) {
              console.error(`Error fetching user ${orderData.userId}:`, userError);
            }
          }

          return {
            id: orderDoc.id,
            ...orderData,
            customerEmail,
            customerName,
          } as Order;
        })
      );

      setOrders(ordersList);
    } catch (error) {
      console.error("Error fetching orders:", error);
      alert("Failed to fetch orders. You might not have permission or there is a network issue.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOrder(null);
    setStatusError(null);
    setStatusSuccess(null);
    setStatusNotes("");
  };

  const downloadOrderData = (order: Order) => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(order, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href",     dataStr);
    downloadAnchorNode.setAttribute("download", `order-${order.id}.json`);
    document.body.appendChild(downloadAnchorNode); // required for firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    setStatusError(null);
    setStatusSuccess(null);
    setStatusUpdating(true);
    try {
      await updateOrderStatus(orderId, newStatus, {
        reason: "admin-action",
        notes: statusNotes || undefined,
        userId: user?.email || undefined,
      });
      // Update local state
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
      if (selectedOrder?.id === orderId) {
        setSelectedOrder((prev) => prev ? { ...prev, status: newStatus } : prev);
      }
      setStatusSuccess(`Status updated to "${getStatusLabel(newStatus)}"`);
      setStatusNotes("");
    } catch (error: any) {
      setStatusError(error.message || "Failed to update status");
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Artist Admin Dashboard
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 4 }}>
        Manage and view customer orders
      </Typography>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="orders table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell>Date</TableCell>
              <TableCell>Order ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Items</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow
                key={order.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {new Date(order.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>{order.id.slice(0, 8)}...</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {order.customerName || "N/A"}
                  </Typography>
                  {order.customerEmail && (
                    <Typography variant="caption" color="text.secondary">
                      {order.customerEmail}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(order.status)}
                    color={getStatusColor(order.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  ${order.amount.toFixed(2)} {order.currency}
                </TableCell>
                <TableCell>
                    {order.items.map((item, idx) => (
                        <div key={idx} style={{ marginBottom: '4px' }}>
                            {item.quantity}x {item.name}
                        </div>
                    ))}
                </TableCell>
                <TableCell align="right">
                  {order.customerEmail && (
                    <Button 
                      startIcon={<EmailIcon />} 
                      size="small" 
                      color="primary"
                      href={`mailto:${order.customerEmail}?subject=Regarding Your Order ${order.id.slice(0, 8)}`}
                      sx={{ mr: 1 }}
                    >
                      Email
                    </Button>
                  )}
                  <Button 
                    startIcon={<VisibilityIcon />} 
                    size="small" 
                    onClick={() => handleViewOrder(order)}
                    sx={{ mr: 1 }}
                  >
                    View
                  </Button>
                  <Button 
                    startIcon={<DownloadIcon />} 
                    size="small" 
                    variant="outlined"
                    onClick={() => downloadOrderData(order)}
                  >
                    JSON
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
                <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        No orders found.
                    </TableCell>
                </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={-1}
        page={page}
        onPageChange={(_, newPage) => {
          if (newPage > page && hasMore) {
            setPage(newPage);
            fetchOrders(rowsPerPage, lastDoc);
          } else if (newPage < page) {
            // Go back to first page (Firestore doesn't support backward pagination easily)
            setPage(0);
            fetchOrders(rowsPerPage);
          }
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          setRowsPerPage(newSize);
          setPage(0);
          fetchOrders(newSize);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelDisplayedRows={({ from, to }) =>
          `${from}–${to}${hasMore ? " (more available)" : ""}`
        }
        nextIconButtonProps={{ disabled: !hasMore }}
      />

      {/* Order Detail Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Order Details: {selectedOrder?.id}
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box>
                {/* Customer Contact Information Section */}
                {(selectedOrder.customerEmail || selectedOrder.customerName) && (
                  <Card sx={{ mb: 3, backgroundColor: '#f0f7ff', border: '1px solid #2196f3' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Customer Contact
                      </Typography>
                      <Typography variant="body1">
                        <strong>Name:</strong> {selectedOrder.customerName || "Not available"}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>Email:</strong> {selectedOrder.customerEmail || "Not available"}
                      </Typography>
                      {selectedOrder.customerEmail && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EmailIcon />}
                          href={`mailto:${selectedOrder.customerEmail}?subject=Regarding Your Order ${selectedOrder.id.slice(0, 8)}`}
                          size="small"
                        >
                          Email Customer
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="h6" gutterBottom>Info</Typography>
                        <p><strong>Date:</strong> {new Date(selectedOrder.createdAt).toLocaleString()}</p>
                        <p>
                          <strong>Status:</strong>{" "}
                          <Chip
                            label={getStatusLabel(selectedOrder.status)}
                            color={getStatusColor(selectedOrder.status)}
                            size="small"
                          />
                        </p>
                        <p><strong>Total:</strong> ${selectedOrder.amount.toFixed(2)} {selectedOrder.currency}</p>
                        <p><strong>User ID:</strong> {selectedOrder.userId}</p>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                         <Typography variant="h6" gutterBottom>PayPal Info</Typography>
                         <p><strong>PayPal Order ID:</strong> {selectedOrder.paypalOrderId}</p>
                    </Grid>

                    {/* Status Update Controls */}
                    <Grid size={{ xs: 12 }}>
                      <Card sx={{ backgroundColor: "#fff8e1", border: "1px solid #ffb300" }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Update Status
                          </Typography>
                          {statusError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatusError(null)}>
                              {statusError}
                            </Alert>
                          )}
                          {statusSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatusSuccess(null)}>
                              {statusSuccess}
                            </Alert>
                          )}
                          <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
                            <TextField
                              label="Notes (optional)"
                              size="small"
                              value={statusNotes}
                              onChange={(e) => setStatusNotes(e.target.value)}
                              sx={{ minWidth: 250, flex: 1 }}
                            />
                          </Box>
                          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                            {ALL_STATUSES.filter(
                              (s) => s !== selectedOrder.status && isValidStatusTransition(selectedOrder.status, s)
                            ).map((targetStatus) => (
                              <Button
                                key={targetStatus}
                                variant="outlined"
                                size="small"
                                color={getStatusColor(targetStatus)}
                                disabled={statusUpdating}
                                onClick={() => handleStatusChange(selectedOrder.id, targetStatus)}
                              >
                                {statusUpdating ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                {getStatusLabel(targetStatus)}
                              </Button>
                            ))}
                            {ALL_STATUSES.filter(
                              (s) => s !== selectedOrder.status && isValidStatusTransition(selectedOrder.status, s)
                            ).length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                No status transitions available (terminal state).
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Items</Typography>
                        {selectedOrder.items.map((item, idx) => (
                            <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" component="div">
                                        {item.name} (x{item.quantity})
                                    </Typography>
                                    <Typography color="text.secondary">
                                        Price: ${item.price}
                                    </Typography>
                                    {item.selectedOptions && (
                                        <Box sx={{ mt: 1, p: 1, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                                            <Typography variant="caption" display="block">
                                                Options:
                                            </Typography>
                                            <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(item.selectedOptions, null, 2)}
                                            </pre>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </Grid>

                    {/* Display Form Data if available */}
                     <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Form Data</Typography>
                        {selectedOrder.formData && Object.keys(selectedOrder.formData).length > 0 ? (
                             <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, overflowX: 'auto' }}>
                                <pre style={{ margin: 0 }}>
                                    {JSON.stringify(selectedOrder.formData, null, 2)}
                                </pre>
                            </Box>
                        ) : (
                            <Typography color="text.secondary">No form data submitted yet.</Typography>
                        )}
                    </Grid>
                </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
          {selectedOrder && (
             <Button 
                variant="contained" 
                startIcon={<DownloadIcon />}
                onClick={() => downloadOrderData(selectedOrder)}
             >
                Download JSON
             </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
